import { GATE_ONLY_MENU_KEYS, MENU_KEY_ALIASES, SUPER_ADMIN_ONLY_MENU_KEYS } from '@/lib/menu/menuConfig';
import { closedKeysFor } from '@/lib/menu/menuGate';
import { isAdministrator, isSuperAdmin, type Session } from '@/lib/types';

/**
 * "이 화면을 보여줄지"를 정하는 순수 판단. JSX·라우터·DOM 을 모른다 — 세션 상태를 받아 결정만 돌려준다.
 *
 * **인가가 아니다.** 브라우저(또는 앱)에서 도는 코드는 사용자가 바꿀 수 있으므로, 이 판단은
 * "권한 없는 화면이 빈 채로 뜨는" UX 문제를 없애는 일일 뿐이다. 실제 인가는 백엔드가 매 요청마다 한다 —
 * 이 로직을 통째로 지워도 데이터가 새어 나가서는 안 된다.
 *
 * 판단을 순수 함수로 떼어 둔 이유: 웹은 이 결정을 react-router 리다이렉트로, 추후 네이티브 앱은 자기
 * 내비게이션으로 옮긴다. 결정 로직은 같고 그리는 방식만 다르므로, 여기엔 화면 기술을 섞지 않는다.
 * (배경: docs/04_2026-07-28_cross-platform-mobile-design.md)
 *
 * ## 세 상태를 뭉뚱그리지 않는다
 *
 * "세션 조회 에러"와 "로그인 안 됨"을 한 갈래로 묶으면, 서버가 잠깐 죽었을 때 로그인으로 튕겼다가
 * 살아나기를 기다리며 화면이 깜빡인다. 그래서 아래로 가른다.
 *  - `pending`        — 세션을 아직 한 번도 못 받음(첫 조회 중).
 *  - `unreachable`    — 서버에 닿지 못함(연결 실패·5xx). 로그아웃이 아니므로 로그인으로 보내지 않는다.
 *  - `unauthenticated`— 서버가 "로그인 안 됨"이라고 분명히 답함.
 *  - `denied`         — 로그인은 했으나 그 메뉴 접근 권한이 없음(로그인으로 보내면 무한 왕복).
 *  - `granted`        — 화면을 그린다.
 */
export type AccessDecision =
  | { kind: 'pending' }
  | { kind: 'unreachable' }
  | { kind: 'unauthenticated' }
  | { kind: 'denied'; menuKey: string }
  | { kind: 'granted' };

export type AccessInput = {
  /** `useSession()` 이 돌려준 것들. */
  session: Session | undefined;
  isPending: boolean;
  isError: boolean;
  /** 이 화면이 걸린 메뉴 키. 없으면 로그인 여부만 본다. */
  menuKey?: string;
};

export function resolveAccess({ session, isPending, isError, menuKey }: AccessInput): AccessDecision {
  // 1) 세션을 아직 한 번도 받지 못했다.
  if (isPending) return { kind: 'pending' };

  // 2) 서버에 닿지 못해 세션을 알 수 없다(연결 실패·5xx로 데이터가 아예 없음).
  //    (TanStack Query v5 는 데이터가 있으면 background refetch 실패에도 status 를 error 로 올리지 않으므로,
  //     여기 걸리는 것은 '한 번도 세션을 받지 못한' 경우뿐이다. 사용 중 서버가 죽으면 마지막 세션이 유지된다.)
  if (isError || !session) return { kind: 'unreachable' };

  // 3) 서버가 "로그인 안 됨"이라고 분명히 답했다.
  if (!session.authenticated) return { kind: 'unauthenticated' };

  // 아직 열지 않은 화면(오픈 게이트 · V153). 권한보다 <b>먼저</b> 본다 — 권한은 그대로 살아 있고
  // 덮개만 덮은 것이라, 뒤에 두면 권한을 가진 사람에게는 게이트가 없는 것과 같아진다.
  // 관리자에게는 빈 집합이 오므로 이 줄을 그냥 지나간다(`closedKeysFor`).
  if (menuKey && closedKeysFor(session).has(menuKey)) {
    return { kind: 'denied', menuKey };
  }

  // 로그인은 했는데 권한이 없는 경우. 최고 관리자 전용 화면은 super_admin 이 아니면(관리자라도) 막는다 —
  // 백엔드도 역할로 막지만, 가드가 없으면 관리자가 들어가 403 화면을 보게 되므로 홈에서 사유를 안내하는 편이 낫다.
  if (menuKey && SUPER_ADMIN_ONLY_MENU_KEYS.has(menuKey) && !isSuperAdmin(session.user?.userType)) {
    return { kind: 'denied', menuKey };
  }

  // 관리자는 전 화면에 접근한다. visibleMenuKeys 는 비관리자용 목록만 담고 관리자에게는 비어 오므로,
  // 이 우회가 없으면 관리자가 모든 menuKey 화면에서 막힌다. (인가가 아니라 UX 판정임은 그대로다.)
  // 탭으로 합친 화면(주간업무·월간업무)은 자기 키가 아니라 원래 화면들의 키로 열린다 —
  // 하나라도 볼 수 있으면 들어가고, 어느 탭이 보이는지는 화면이 다시 판정한다(`MENU_KEY_ALIASES`).
  // 게이트만 걸리는 화면(AI Agent)은 여기서 끝난다. 로그인한 전원에게 열려 있던 곳이라
  // visibleMenuKeys 에 담겨 오지 않는다 — 아래 줄에 맡기면 게이트를 열어도 아무도 못 들어간다.
  if (menuKey && GATE_ONLY_MENU_KEYS.has(menuKey)) {
    return { kind: 'granted' };
  }

  const isAdmin = isAdministrator(session.user?.userType);
  const openingKeys = menuKey ? MENU_KEY_ALIASES[menuKey] ?? [menuKey] : [];
  if (menuKey && !isAdmin && !openingKeys.some((key) => session.visibleMenuKeys.includes(key))) {
    return { kind: 'denied', menuKey };
  }

  return { kind: 'granted' };
}
